import React from 'react';
import PropTypes from 'prop-types';
import { groupsPropType, badgesPropType, groupBadgesPropType } from './propTypes';
import useNavItemAccess from './useNavItemAccess';
import * as Styled from './SubNav.styles';

/**
 * Collapsible left sub-navigation driven by a `groups` config.
 * Shared by the redesigned RetailerView and CampaignView pages.
 *
 * `badges` are keyed by item key (per-tab counts/dots); `groupBadges` are keyed by group key and
 * render a pill next to the group label (e.g. an applied-filters count). Both are optional.
 */
const SubNav = ({ groups, activeKey, activeKeys, badges, groupBadges, onSelect, collapsed }) => {
  // Role permission AND account module — see useNavItemAccess.
  const hasPermission = useNavItemAccess();
  // Items to highlight. Defaults to the single `activeKey`, but a nav with two independent axes
  // (e.g. the active section + the active List/Map view) can pass several.
  const highlightedKeys = activeKeys.length ? activeKeys : [activeKey];

  return (
    <nav>
      {groups.map((group) => {
        const visibleItems = group.items.filter(hasPermission);
        if (!visibleItems.length) return null;

        const groupBadge = groupBadges[group.key];
        const GroupBadgeIcon = groupBadge?.icon;

        return (
          <Styled.Group key={group.key}>
            <Styled.GroupLabel collapsed={collapsed}>
              <span>{group.label}</span>
              {groupBadge != null && (
                <Styled.GroupBadge tone={groupBadge.tone}>
                  {GroupBadgeIcon && <GroupBadgeIcon />}
                  {groupBadge.value}
                </Styled.GroupBadge>
              )}
            </Styled.GroupLabel>
            {visibleItems.map((item) => {
              const Icon = item.icon;
              const badge = badges[item.key];
              return (
                <Styled.Item
                  key={item.key}
                  type="button"
                  active={highlightedKeys.includes(item.key)}
                  sub={item.sub}
                  collapsed={collapsed}
                  onClick={() => onSelect(item.key)}
                  title={item.label}
                >
                  {Icon && <Icon />}
                  <Styled.ItemLabel collapsed={collapsed}>{item.label}</Styled.ItemLabel>
                  {badge != null && !collapsed && <Styled.Badge tone={badge.tone}>{badge.value}</Styled.Badge>}
                  {badge != null && collapsed && <Styled.Dot tone={badge.tone} />}
                </Styled.Item>
              );
            })}
          </Styled.Group>
        );
      })}
    </nav>
  );
};

SubNav.propTypes = {
  groups: groupsPropType.isRequired,
  activeKey: PropTypes.string.isRequired,
  activeKeys: PropTypes.arrayOf(PropTypes.string),
  badges: badgesPropType,
  groupBadges: groupBadgesPropType,
  onSelect: PropTypes.func.isRequired,
  collapsed: PropTypes.bool,
};

SubNav.defaultProps = {
  activeKeys: [],
  badges: {},
  groupBadges: {},
  collapsed: false,
};

export default SubNav;
